import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api.js';
import { useAuth } from '../../context/AuthContext.js';
import { useToast } from '../../context/ToastContext.js';
import { useAppConfig } from '../../context/AppConfigContext.js';
import { BigButton } from '../../components/BigButton.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Page, PageHeader, Field } from '../../components/layout.js';

function formatServerTime(iso: string): string {
  return new Intl.DateTimeFormat('en-MY', {
    dateStyle: 'medium',
    timeStyle: 'medium',
    timeZone: 'Asia/Kuala_Lumpur',
  }).format(new Date(iso));
}

export function SettingsPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { environment } = useAppConfig();
  const navigate = useNavigate();

  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  function openResetDialog() {
    setResetError(null);
    setConfirmResetOpen(true);
  }

  function closeResetDialog() {
    if (resetting) return;
    setConfirmResetOpen(false);
    setResetError(null);
    setResetPassword('');
    setResetPasswordVisible(false);
  }

  async function handleTrainingReset() {
    if (!resetPassword) return;
    setResetting(true);
    setResetError(null);
    try {
      await api.trainingReset(resetPassword);
      setConfirmResetOpen(false);
      setResetPassword('');
      setResetPasswordVisible(false);
      showToast('Training data cleared', 'success');
      // Unlike a demo re-seed, clearing keeps the training logins and every
      // existing session (including this one) valid - no sign-out, so we go
      // straight to the now-empty Products list rather than back to /login.
      navigate('/admin/products', { replace: true });
    } catch (err) {
      // Keep the dialog open and the typed password in place so the admin
      // can just fix it and retry, rather than re-typing everything.
      setResetError(err instanceof ApiError ? err.message : 'Could not clear training data');
    } finally {
      setResetting(false);
    }
  }

  // Second, distinct training action: reset stock/sales only, keeping the
  // product catalogue (names, photos, categories) - for repeat training
  // cycles where the owner wants trainees to restock and sell again without
  // re-creating anything. Mirrors the full-reset state/handlers above rather
  // than sharing them, since the two dialogs can be open independently and
  // call different endpoints.
  const [confirmStockResetOpen, setConfirmStockResetOpen] = useState(false);
  const [stockResetting, setStockResetting] = useState(false);
  const [stockResetPassword, setStockResetPassword] = useState('');
  const [stockResetPasswordVisible, setStockResetPasswordVisible] = useState(false);
  const [stockResetError, setStockResetError] = useState<string | null>(null);

  function openStockResetDialog() {
    setStockResetError(null);
    setConfirmStockResetOpen(true);
  }

  function closeStockResetDialog() {
    if (stockResetting) return;
    setConfirmStockResetOpen(false);
    setStockResetError(null);
    setStockResetPassword('');
    setStockResetPasswordVisible(false);
  }

  async function handleTrainingStockReset() {
    if (!stockResetPassword) return;
    setStockResetting(true);
    setStockResetError(null);
    try {
      await api.trainingResetStock(stockResetPassword);
      setConfirmStockResetOpen(false);
      setStockResetPassword('');
      setStockResetPasswordVisible(false);
      showToast('Stock and sales reset', 'success');
      // Products/categories/logins are untouched and the caller stays
      // signed in, but every product's stock and the sales list changed -
      // refresh whichever admin page is showing that data.
      navigate('/admin/products', { replace: true });
    } catch (err) {
      setStockResetError(err instanceof ApiError ? err.message : 'Could not reset stock and sales');
    } finally {
      setStockResetting(false);
    }
  }

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [serverTime, setServerTime] = useState<string | null>(null);

  useEffect(() => {
    void api
      .health()
      .then((r) => setServerTime(r.time))
      .catch(() => {});
  }, []);

  const confirmMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    newPassword === confirmPassword &&
    !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.changePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showToast('Password changed', 'success');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change your password');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Page>
      <PageHeader title="Settings" />

      <section className="card card-stack form-width">
        <h2 className="section-title">Change your password</h2>
        <p className="font-body text-sm text-muted">Signed in as {user?.displayName}</p>

        <Field label="Current password" htmlFor="current-password">
          <input
            id="current-password"
            type={visible ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="input"
          />
        </Field>

        <Field label="New password (min 8 characters)" htmlFor="new-password-settings">
          <input
            id="new-password-settings"
            type={visible ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="input"
          />
        </Field>

        <Field label="Confirm new password" htmlFor="confirm-password">
          <input
            id="confirm-password"
            type={visible ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            aria-invalid={confirmMismatch ? true : undefined}
            className="input"
          />
          {confirmMismatch && (
            <p role="alert" className="font-body text-sm text-danger">
              Passwords do not match
            </p>
          )}
        </Field>

        <label className="flex items-center gap-3 font-body text-base text-text min-h-12">
          <input
            type="checkbox"
            checked={visible}
            onChange={(e) => setVisible(e.target.checked)}
            className="h-6 w-6 min-h-0 accent-teal shrink-0"
          />
          Show passwords
        </label>

        {error && (
          <p role="alert" className="font-body text-sm text-danger">
            {error}
          </p>
        )}

        <BigButton disabled={!canSubmit} onClick={() => void handleSubmit()}>
          {submitting ? 'Saving…' : 'Change password'}
        </BigButton>
      </section>

      {environment === 'training' && (
        <section className="card card-stack form-width">
          <h2 className="section-title">Training data</h2>

          <div className="flex flex-col gap-2">
            <p className="font-body text-base font-semibold text-text">Reset stock and sales</p>
            <p className="font-body text-sm text-muted">
              Sets every product&apos;s stock to 0 and deletes all sales history. Products, photos
              and logins are kept. Use this between training runs.
            </p>
            <BigButton variant="secondary" onClick={openStockResetDialog}>
              Reset stock and sales
            </BigButton>
          </div>

          <div className="flex flex-col gap-2 pt-2 border-t border-border">
            <p className="font-body text-base font-semibold text-text">Clear all training data</p>
            <p className="font-body text-sm text-muted">
              Resetting clears all products, categories, sales and photos. The training logins
              (trainer, staff1, staff2) are kept, and you&apos;ll stay signed in. This cannot be
              undone.
            </p>
            <BigButton variant="danger" onClick={openResetDialog}>
              Reset training data now
            </BigButton>
          </div>

          <ConfirmDialog
            open={confirmStockResetOpen}
            title="Reset stock and sales?"
            description={
              <div className="flex flex-col gap-4">
                <p>
                  Sets every product&apos;s stock to 0 and deletes all sales history. Products,
                  photos and logins are kept. This cannot be undone.
                </p>
                <Field label="Your password" htmlFor="stock-reset-password">
                  <div className="relative">
                    <input
                      id="stock-reset-password"
                      type={stockResetPasswordVisible ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={stockResetPassword}
                      onChange={(e) => setStockResetPassword(e.target.value)}
                      className="input pr-20 text-base"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setStockResetPasswordVisible((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 min-h-12 px-3 rounded-md font-body font-semibold text-base text-teal-dark"
                    >
                      {stockResetPasswordVisible ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </Field>
                {stockResetError && (
                  <p role="alert" className="font-body text-sm text-danger">
                    {stockResetError}
                  </p>
                )}
              </div>
            }
            confirmLabel={stockResetting ? 'Resetting…' : 'Reset now'}
            variant="danger"
            confirmDisabled={stockResetPassword.length === 0 || stockResetting}
            onConfirm={() => void handleTrainingStockReset()}
            onCancel={closeStockResetDialog}
          />

          <ConfirmDialog
            open={confirmResetOpen}
            title="Reset training data?"
            description={
              <div className="flex flex-col gap-4">
                <p>
                  Clears all products, categories, sales and photos. The training logins are kept.
                  This cannot be undone.
                </p>
                <Field label="Your password" htmlFor="reset-password">
                  <div className="relative">
                    <input
                      id="reset-password"
                      type={resetPasswordVisible ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      className="input pr-20 text-base"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setResetPasswordVisible((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 min-h-12 px-3 rounded-md font-body font-semibold text-base text-teal-dark"
                    >
                      {resetPasswordVisible ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </Field>
                {resetError && (
                  <p role="alert" className="font-body text-sm text-danger">
                    {resetError}
                  </p>
                )}
              </div>
            }
            confirmLabel={resetting ? 'Resetting…' : 'Reset now'}
            variant="danger"
            confirmDisabled={resetPassword.length === 0 || resetting}
            onConfirm={() => void handleTrainingReset()}
            onCancel={closeResetDialog}
          />
        </section>
      )}

      <section className="card flex flex-col gap-1 form-width">
        <h2 className="section-title">App info</h2>
        <p className="font-body text-sm text-muted">Divergent Works</p>
        <p className="font-body text-sm text-muted">Version {__APP_VERSION__}</p>
        <p className="font-body text-sm text-muted">
          Server time: {serverTime ? formatServerTime(serverTime) : '—'}
        </p>
      </section>
    </Page>
  );
}
